import React, { useRef } from 'react';

import {
  Box,
  Text,
  VStack,
  Button,
  HStack,
  Icon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaFileCsv, FaUpload, FaTimes } from 'react-icons/fa';

import { formatDateTime } from 'utils/date.util';

import { useColors } from './colors';

const MappingUploadSection = ({
  mappingFile,
  savedMapping,
  onMappingFileChange,
  onFileSelected,
  previewRows = [],
  embedded = false,
}) => {
  const { textColor, backgroundCardColor, borderColor, secondaryTextColor, mutedBg } = useColors();
  const inputRef = useRef(null);

  const activeMapping = mappingFile || (savedMapping?.exists ? savedMapping : null);
  const isSavedOnly = !mappingFile && savedMapping?.exists;

  const handleFile = (file) => {
    if (!file) return;
    onMappingFileChange({
      name: file.name,
      size: file.size,
      uploadedAt: new Date().toISOString(),
    });
    if (onFileSelected) {
      onFileSelected(file);
    }
  };

  const clearFile = () => {
    onMappingFileChange(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const displayName = activeMapping?.fileName || activeMapping?.name || 'mapping.csv';
  const displaySize = activeMapping?.fileSizeBytes ?? activeMapping?.size;
  const displayUploadedAt = activeMapping?.uploadedAt;
  const entryCount = activeMapping?.storedEntryCount ?? activeMapping?.entryCount;

  const body = (
    <>
      {!embedded && (
        <>
          <Text color={textColor} fontSize="md" fontWeight="semibold" mb={1}>
            Supplier SKU → Partslink mapping
          </Text>
          <Text color={secondaryTextColor} fontSize="sm" mb={4}>
            Upload a CSV with exactly two columns: <strong>sku</strong> and <strong>partslink</strong>.
            Used to validate listings before fetching verified data.
          </Text>
        </>
      )}
      {embedded && (
        <Text color={secondaryTextColor} fontSize="sm" mb={4}>
          Upload a CSV with exactly two columns: <strong>sku</strong> and <strong>partslink</strong>.
          Used to validate listings before fetching verified data.
        </Text>
      )}

      {savedMapping?.exists && (
        <Alert status="info" borderRadius="md" mb={4} alignItems="flex-start">
          <AlertIcon mt={1} />
          <Box>
            <Text fontSize="sm" fontWeight="semibold">
              Last saved mapping
            </Text>
            <Text fontSize="sm" color={secondaryTextColor}>
              {savedMapping.fileName || 'supplier-sku-partslink-mapping.json'}
              {entryCount != null ? ` · ${entryCount.toLocaleString()} rows` : ''}
              {savedMapping.uploadedAt
                ? ` · Updated ${formatDateTime(savedMapping.uploadedAt)}`
                : ''}
            </Text>
          </Box>
        </Alert>
      )}

      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(e) => handleFile(e.target.files?.[0])}
      />

      {!activeMapping ? (
        <Box
          p={8}
          borderWidth="2px"
          borderStyle="dashed"
          borderColor={borderColor}
          borderRadius="lg"
          bg={mutedBg}
          textAlign="center"
        >
          <Icon as={FaFileCsv} boxSize={10} color="teal.500" mb={3} />
          <Text color={textColor} fontWeight="medium" mb={2}>
            Drop CSV here or browse
          </Text>
          <Text color={secondaryTextColor} fontSize="sm" mb={4}>
            Required columns: sku, partslink
          </Text>
          <Button
            leftIcon={<FaUpload />}
            colorScheme="teal"
            size="sm"
            onClick={() => inputRef.current?.click()}
          >
            Choose file
          </Button>
        </Box>
      ) : (
        <VStack align="stretch" spacing={3}>
          <HStack
            p={3}
            bg={mutedBg}
            borderRadius="md"
            justify="space-between"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <HStack align="flex-start">
              <Icon as={FaFileCsv} color="teal.500" mt={1} />
              <Box>
                <Text color={textColor} fontSize="sm" fontWeight="medium">
                  {displayName}
                </Text>
                <Text color={secondaryTextColor} fontSize="xs">
                  {displaySize != null ? `${(displaySize / 1024).toFixed(1)} KB` : ''}
                  {entryCount != null ? ` · ${entryCount.toLocaleString()} rows` : ''}
                  {displayUploadedAt ? ` · ${formatDateTime(displayUploadedAt)}` : ''}
                </Text>
                <Text color={secondaryTextColor} fontSize="xs" mt={1}>
                  {isSavedOnly
                    ? 'Loaded from server — upload a new file to replace'
                    : 'Ready for run'}
                </Text>
              </Box>
            </HStack>
            {!isSavedOnly && (
              <Button size="sm" variant="ghost" leftIcon={<FaTimes />} onClick={clearFile}>
                Remove
              </Button>
            )}
            {isSavedOnly && (
              <Button
                size="sm"
                variant="outline"
                colorScheme="teal"
                leftIcon={<FaUpload />}
                onClick={() => inputRef.current?.click()}
              >
                Replace
              </Button>
            )}
          </HStack>

          {previewRows.length > 0 && (
            <TableContainer borderWidth="1px" borderColor={borderColor} borderRadius="md">
              <Table size="sm">
                <Thead bg={mutedBg}>
                  <Tr>
                    <Th>sku</Th>
                    <Th>partslink</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {previewRows.map((row, idx) => (
                    <Tr key={idx}>
                      <Td fontFamily="mono" fontSize="xs">
                        {row.sku}
                      </Td>
                      <Td fontFamily="mono" fontSize="xs">
                        {row.partslink}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}

          <Alert status="success" size="sm" borderRadius="md">
            <AlertIcon />
            <Text fontSize="sm">
              {isSavedOnly
                ? 'Mapping is stored on the server and will be used for the next run.'
                : 'Mapping uploaded and saved. Preview shows the first rows.'}
            </Text>
          </Alert>
        </VStack>
      )}

      <HStack mt={3} spacing={2}>
        <Badge colorScheme="gray" variant="subtle">
          .csv
        </Badge>
        <Badge colorScheme="gray" variant="subtle">
          sku, partslink
        </Badge>
      </HStack>
    </>
  );

  if (embedded) {
    return body;
  }

  return (
    <Box
      p={5}
      bg={backgroundCardColor}
      borderRadius="lg"
      borderWidth="1px"
      borderColor={borderColor}
      boxShadow="sm"
    >
      {body}
    </Box>
  );
};

export default MappingUploadSection;
